import {
  FormControl,
  FormHelperText,
  FormLabel,
  InputProps,
  Skeleton,
} from '@mui/joy'
import { isValid } from 'date-fns'
import ru from 'date-fns/locale/ru'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import type { InputMaskElement } from 'imask'
import { ChangeEvent, forwardRef } from 'react'
import DatePicker from 'react-datepicker'
import { useFormContext } from 'react-hook-form'
import { IMaskInput } from 'react-imask'
import { uiDateFormat } from '~/shared/config/datetime'
import { formatForApi } from '~/shared/lib/date'
import { CalendarIcon } from '~/shared/ui/Icons'
import { DatePickerPopper, Input } from '../ui/styled'
import 'react-datepicker/dist/react-datepicker.css'

export type DatePickerInputProps = {
  name: string
  skeletonShow?: boolean
  label?: string
} & Omit<InputProps, 'name' | 'slotProps'>

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
}

const MaskAdapter = forwardRef<HTMLElement, CustomProps>(function Wrapper(
  { onChange, ...other },
  ref: InputMaskElement,
) {
  return (
    <IMaskInput
      {...other}
      mask='##.##.####'
      definitions={{
        '#': /[0-9]/,
      }}
      onInput={(value: ChangeEvent<HTMLInputElement>) => onChange(value)}
      inputRef={ref}
      autoComplete='off'
    />
  )
})

export function DatePickerInput({
  name,
  skeletonShow,
  label,
  placeholder,
  ...inputProps
}: DatePickerInputProps) {
  const {
    setValue,
    formState: { errors },
    watch,
    trigger,
  } = useFormContext()
  const selected = watch(name) || null

  const handleDateChange = (date: Date | null) => {
    setValue(name, isValid(date) ? formatForApi(date as Date) : '')
  }

  const messageError = errors[name]?.message as string
  const isValidateError = Boolean(errors[name])

  watch((_, { name: watchName }) => {
    if (watchName === name && isValidateError) {
      trigger(name)
    }
  })

  return (
    <FormControl
      error={isValidateError}
      sx={{
        position: 'initial',
      }}
    >
      {label && <FormLabel>{label}</FormLabel>}
      <DatePicker
        selected={selected}
        onChange={handleDateChange}
        dateFormat={uiDateFormat}
        locale={ru}
        placeholderText={placeholder}
        popperContainer={DatePickerPopper}
        disabledKeyboardNavigation
        readOnly={inputProps.readOnly}
        customInput={
          <Input
            {...inputProps}
            startDecorator={<CalendarIcon />}
            name={name}
            fullWidth
            error={isValidateError}
            size='lg'
            slotProps={{
              ...(skeletonShow ? { root: { component: Skeleton } } : {}),
              input: {
                component: MaskAdapter,
                name,
              },
            }}
          />
        }
      />
      {isValidateError && (
        <FormHelperText data-testid={`error-message-${label || placeholder}`}>
          {messageError}
        </FormHelperText>
      )}
    </FormControl>
  )
}
